// Журнал действий: список и выгрузка (contracts/api.md; FR-034 – FR-035; принцип XI, T112, T141);
// выгрузка в фоне и «Мои выгрузки» (002: contracts/api.md; FR-051 – FR-056).
import { z } from '../kernel/zod.ts';
import { AuditEntrySchema, AuditResultSchema } from '../audit/entry.ts';
import { AuditExportSchema } from '../audit/export.ts';
import { IdSchema, TimestampInputSchema } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';

/** Выгрузка сразу — до 10 000 записей; больше — 413 REC-008 (FR-034б). */
export const AUDIT_EXPORT_LIMIT = 10_000;

export const XLSX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Сортировка журнала — по времени действия: at — старые сверху, -at — новые сверху. */
export const auditSortFields = ['at'] as const;

/**
 * Поиск и фильтры — одни у списка и выгрузки. q — часть ФИО автора или названия записи (ФИО
 * пользователя, назначения, замещения, временного доступа, роль словом), без учёта регистра,
 * «ё» как «е». aboutUser — записи о пользователе и его назначениях («Все →» из карточки).
 * Период — моменты времени, границы включительно.
 */
export const AuditFiltersSchema = z.object({
  q: z.string().trim().max(200).optional(),
  actor: IdSchema.optional(),
  operation: z.string().trim().min(1).max(100).optional(),
  entityType: z.string().trim().min(1).max(100).optional(),
  entityId: IdSchema.optional(),
  /** Несколько записей через запятую, до 100 — «История» карточки (006, FR-027); только сужает. */
  entityIds: z
    .string()
    .transform((s) => s.split(',').filter(Boolean))
    .pipe(z.array(IdSchema).min(1).max(100))
    .optional(),
  aboutUser: IdSchema.optional(),
  from: TimestampInputSchema.optional(),
  to: TimestampInputSchema.optional(),
  result: AuditResultSchema.optional(),
});

const AuditSortSchema = sortSchema(auditSortFields).default('-at');

const periodInOrder = (v: { from?: string | undefined; to?: string | undefined }): boolean =>
  v.from === undefined || v.to === undefined || Date.parse(v.from) <= Date.parse(v.to);

/** GET /api/audit. */
export const AuditListQuerySchema = z
  .object({ ...PageQuerySchema.shape, ...AuditFiltersSchema.shape, sort: AuditSortSchema })
  .refine(periodInOrder, { path: ['to'], error: fieldMessages.period });
export type AuditListQuery = z.input<typeof AuditListQuerySchema>;

/** GET /api/audit/export: те же поиск, фильтры и сортировка, без страницы. */
export const AuditExportQuerySchema = z
  .object({ ...AuditFiltersSchema.shape, sort: AuditSortSchema })
  .refine(periodInOrder, { path: ['to'], error: fieldMessages.period });
export type AuditExportQuery = z.input<typeof AuditExportQuerySchema>;

export const AuditPageSchema = pageSchema(AuditEntrySchema);

/**
 * POST /api/audit/exports: те же поиск, фильтры и сортировка, что у выгрузки сразу. Фильтры
 * хранятся в заказе, в задачу идёт только номер заказа (FR-010а).
 */
export const CreateAuditExportBodySchema = AuditExportQuerySchema;
export type CreateAuditExportBody = z.input<typeof CreateAuditExportBodySchema>;

/**
 * GET /api/audit/exports: свои заказы, новые сверху (FR-056), и срок хранения готового файла
 * в днях — из настроек установки (retention.exportFileDays, FR-055): экран его не зашивает.
 */
export const AuditExportsQuerySchema = PageQuerySchema;
export const AuditExportsPageSchema = pageSchema(AuditExportSchema).extend({
  retentionDays: z.number().int().positive(),
});
export type AuditExportsPage = z.infer<typeof AuditExportsPageSchema>;
